import {useEffect, useState} from "react";
import moment from "moment";

import Button from "@mui/material/Button";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Checkbox,
	FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFTextField from "@/components/Input/TextField";

import {PlayerAPI} from "@/api/player";
import {
	CONVOCATION_WEBHOOK_URL,
	DEFAULT_CATEGORY_ID,
	EMAILJS_SERVICE_ID,
	EMAILJS_TEMPLATE_ID,
	EMAILJS_PUBLIC_KEY,
	ERROR_OPERATION,
} from "@/utils/constants";
import {
	buildConvocationText,
	buildMailto,
	sendConvocationWebhook,
	sendConvocationEmailJS,
} from "@/utils/convocation";

const HAS_EMAILJS =
	EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY;

// Definito a livello di modulo: se stesse dentro il componente verrebbe
// ricreato a ogni render, smontando gli input (perdita del focus).
const Section = ({title, children}) => (
	<div
		style={{border: "1px solid #ebf1fd", borderRadius: 12, padding: 12}}
		className="mt-2"
	>
		<div style={{fontWeight: 700, marginBottom: 8}}>{title}</div>
		{children}
	</div>
);

// Convocazione a provino: ricerca/crea ragazzo, dati provino, contatti, destinatari,
// testo email, invio (servizio esterno con fallback mailto).
const ConvocationModal = (props) => {
	const {open, onClose, players, categories, handleNotification, onSent} = props;

	const [saveOption, setSaveOption] = useState("select"); // select | new
	const [form, setForm] = useState({});
	const [sending, setSending] = useState(false);
	// true quando l'utente ha modificato a mano oggetto/testo: da lì niente auto-sync
	const [textDirty, setTextDirty] = useState(false);

	const setField = (key, value) => setForm((p) => ({...p, [key]: value}));

	const playerOptions = (players || []).map((p) => ({
		value: p.id,
		label: `${p.lastName} ${p.firstName || ""}`.trim(),
		...p,
	}));
	const categoryOptions = (categories || []).map((c) => ({
		value: c.id || c.value,
		label: c.name || c.label,
	}));

	const playerName =
		saveOption === "new"
			? `${form.firstName || ""} ${form.lastName || ""}`.trim()
			: form.player?.label || "";

	const resolvedTo = form.registered
		? form.clubEmail
		: form.guardianEmail || form.agentEmail;

	const handleClose = () => {
		setSaveOption("select");
		setForm({});
		setSending(false);
		setTextDirty(false);
		onClose();
	};

	const onSelectPlayer = (opt) => {
		const p = (players || []).find((x) => x.id === opt?.value);
		setForm((prev) => ({
			...prev,
			player: opt,
			// prendi i dati dal ragazzo se già presenti, altrimenti lascia da compilare
			registered: p?.registered ?? prev.registered ?? false,
			clubName: p?.membershipClub?.name || p?.clubName || prev.clubName || "",
			clubEmail:
				p?.membershipClub?.email || p?.clubEmail || prev.clubEmail || "",
			guardianName:
				p?.guardians?.[0]?.name || p?.guardianName || prev.guardianName || "",
			guardianEmail:
				p?.guardians?.[0]?.email ||
				p?.guardianEmail ||
				prev.guardianEmail ||
				"",
			agentName: p?.agent?.name || p?.agentName || prev.agentName || "",
			agentEmail: p?.agent?.email || p?.agentEmail || prev.agentEmail || "",
		}));
	};

	const generateText = () => {
		const {subject, body} = buildConvocationText({
			playerName,
			dateStr: form.date ? moment(form.date).format("DD/MM/YYYY") : "",
			timeStr: form.time || "",
			place: form.place || "",
			categoryLabel: form.category?.label || "",
		});
		setForm((prev) => ({...prev, subject, body}));
	};

	// auto-sincronizza oggetto/testo dai campi del form, finché l'utente non li
	// modifica a mano (textDirty). Il pulsante "Rigenera" riazzera textDirty.
	useEffect(() => {
		if (!textDirty) {
			generateText();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playerName, form.date, form.time, form.place, form.category, textDirty]);

	const createProspect = async () => {
		try {
			return await PlayerAPI.create({
				firstName: form.firstName || "",
				lastName: form.lastName,
				dateOfBirth: form.dateOfBirth
					? moment(form.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				goalkeeper: !!form.goalkeeper,
				primaryRole: form.goalkeeper ? "Portiere" : undefined,
				prospect: true,
				playerCategoryId: DEFAULT_CATEGORY_ID,
			});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			return null;
		}
	};

	const canSend =
		(saveOption === "new" ? !!form.lastName : !!form.player) &&
		form.date &&
		form.time &&
		resolvedTo &&
		form.subject &&
		form.body &&
		!sending;

	const send = async () => {
		setSending(true);
		try {
			let playerId = form.player?.value;
			if (saveOption === "new") {
				const created = await createProspect();
				if (!created) {
					setSending(false);
					return;
				}
				playerId = created.id;
			}

			const to = [resolvedTo].filter(Boolean);
			const cc = [
				form.ccCoach,
				form.ccCoordinator,
				form.ccResponsible,
				form.ccExtra,
			].filter(Boolean);

			const payload = {
				to,
				cc,
				subject: form.subject,
				body: form.body,
				tryout: {
					playerId,
					playerName,
					date: form.date ? moment(form.date).format("YYYY-MM-DD") : null,
					time: form.time || null,
					place: form.place || null,
					category: form.category?.label || null,
					registered: !!form.registered,
					club: form.registered
						? {name: form.clubName, email: form.clubEmail}
						: null,
				},
			};

			// 1) EmailJS (se configurato)
			if (HAS_EMAILJS) {
				const ok = await sendConvocationEmailJS({
					serviceId: EMAILJS_SERVICE_ID,
					templateId: EMAILJS_TEMPLATE_ID,
					publicKey: EMAILJS_PUBLIC_KEY,
					to,
					cc,
					subject: form.subject,
					body: form.body,
					playerName,
				}).catch(() => false);
				if (ok) {
					handleNotification("Convocazione inviata.", "success");
					onSent && onSent();
					handleClose();
					return;
				}
				window.location.href = buildMailto({
					to,
					cc,
					subject: form.subject,
					body: form.body,
				});
				handleNotification(
					"Invio non riuscito: aperto il client di posta.",
					"info",
				);
				handleClose();
				return;
			}

			// 2) Webhook esterno (se configurato)
			if (CONVOCATION_WEBHOOK_URL) {
				const ok = await sendConvocationWebhook(
					CONVOCATION_WEBHOOK_URL,
					payload,
				).catch(() => false);
				if (ok) {
					handleNotification("Convocazione inviata.", "success");
					onSent && onSent();
					handleClose();
					return;
				}
				// servizio non raggiungibile -> fallback mailto
				window.location.href = buildMailto({
					to,
					cc,
					subject: form.subject,
					body: form.body,
				});
				handleNotification(
					"Servizio non raggiungibile: aperto il client di posta.",
					"info",
				);
				handleClose();
				return;
			}

			// 3) nessun servizio configurato -> mailto
			window.location.href = buildMailto({
				to,
				cc,
				subject: form.subject,
				body: form.body,
			});
			handleNotification(
				"Aperto il client di posta con la convocazione.",
				"info",
			);
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setSending(false);
		}
	};

	const txt = (key, label, extra = {}) => (
		<VLFTextField
			label={label}
			size="small"
			value={form[key] ?? ""}
			onChange={(e) => setField(key, e.target.value)}
			{...extra}
		/>
	);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{className: "vlf-modal__paper"}}
		>
			<DialogTitle
				className="vlf-modal__title"
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<span className="vlf-modal__title-text">Convoca a provino</span>
				<IconButton size="small" onClick={handleClose} aria-label="chiudi">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent className="vlf-modal__content dialog-container">
				<Section title="Ragazzo">
					<div className="d-flex gap-2 mb-2">
						<Button
							size="small"
							variant={saveOption === "select" ? "contained" : "outlined"}
							onClick={() => setSaveOption("select")}
						>
							Cerca esistente
						</Button>
						<Button
							size="small"
							variant={saveOption === "new" ? "contained" : "outlined"}
							onClick={() => setSaveOption("new")}
						>
							Crea nuovo
						</Button>
					</div>
					{saveOption === "select" ? (
						<VLFSelectNew
							options={playerOptions}
							value={form.player || null}
							onChange={onSelectPlayer}
							placeholder="Cerca il ragazzo…"
						/>
					) : (
						<div className="row no-margin">
							<div className="col-12 col-md-4 padding-small">
								{txt("firstName", "Nome")}
							</div>
							<div className="col-12 col-md-4 padding-small">
								{txt("lastName", "Cognome")}
							</div>
							<div className="col-12 col-md-4 padding-small">
								<VLFDatePicker
									label="Data di nascita"
									value={form.dateOfBirth}
									onChange={(v) => setField("dateOfBirth", v)}
									format="DD-MM-YYYY"
									size="small"
								/>
							</div>
							<div className="col-12 padding-small">
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={!!form.goalkeeper}
											onChange={(e) => setField("goalkeeper", e.target.checked)}
										/>
									}
									label="Portiere"
								/>
							</div>
						</div>
					)}
				</Section>

				<Section title="Provino">
					<div className="row no-margin">
						<div className="col-12 col-md-3 padding-small">
							<VLFDatePicker
								label="Data"
								value={form.date}
								onChange={(v) => setField("date", v)}
								format="DD-MM-YYYY"
								size="small"
								required
							/>
						</div>
						<div className="col-6 col-md-3 padding-small">
							{txt("time", "Ora (HH:MM)", {placeholder: "17:30"})}
						</div>
						<div className="col-6 col-md-3 padding-small">
							{txt("place", "Luogo")}
						</div>
						<div className="col-12 col-md-3 padding-small">
							<VLFSelectNew
								options={categoryOptions}
								value={form.category || null}
								onChange={(v) => setField("category", v)}
								placeholder="Leva/Categoria"
							/>
						</div>
					</div>
				</Section>

				<Section title="Tesseramento & contatti">
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={!!form.registered}
								onChange={(e) => setField("registered", e.target.checked)}
							/>
						}
						label="Tesserato (invio al club di appartenenza)"
					/>
					{form.registered ? (
						<div className="row no-margin">
							<div className="col-12 col-md-6 padding-small">
								{txt("clubName", "Club di appartenenza")}
							</div>
							<div className="col-12 col-md-6 padding-small">
								{txt("clubEmail", "Email club", {type: "email"})}
							</div>
						</div>
					) : (
						<div className="row no-margin">
							<div className="col-12 col-md-3 padding-small">
								{txt("guardianName", "Genitore/tutore")}
							</div>
							<div className="col-12 col-md-3 padding-small">
								{txt("guardianEmail", "Email genitore", {type: "email"})}
							</div>
							<div className="col-12 col-md-3 padding-small">
								{txt("agentName", "Agente")}
							</div>
							<div className="col-12 col-md-3 padding-small">
								{txt("agentEmail", "Email agente", {type: "email"})}
							</div>
						</div>
					)}
				</Section>

				<Section title="Destinatari">
					<div
						style={{fontSize: "0.85rem", marginBottom: 8}}
						className={resolvedTo ? "" : "text-danger"}
					>
						<b>A:</b> {resolvedTo || "— (inserisci l'email del destinatario sopra)"}
					</div>
					<div className="row no-margin">
						<div className="col-12 col-md-4 padding-small">
							{txt("ccCoach", "CC — Allenatore della leva", {type: "email"})}
						</div>
						<div className="col-12 col-md-4 padding-small">
							{txt("ccCoordinator", "CC — Coordinatore", {type: "email"})}
						</div>
						<div className="col-12 col-md-4 padding-small">
							{txt("ccResponsible", "CC — Responsabile", {type: "email"})}
						</div>
						<div className="col-12 padding-small">
							{txt("ccExtra", "CC — Altro (es. interessato)", {type: "email"})}
						</div>
					</div>
				</Section>

				<Section title="Messaggio">
					<div className="d-flex align-items-center justify-content-between mb-1">
						<div style={{fontSize: "0.75rem", color: "#6b7a99"}}>
							{textDirty
								? "Testo modificato a mano (niente aggiornamento automatico)."
								: "Il testo si aggiorna da data/ora/luogo/leva."}
						</div>
						<Button
							size="small"
							onClick={() => {
								setTextDirty(false);
								generateText();
							}}
						>
							Rigenera testo dal template
						</Button>
					</div>
					<div className="row no-margin">
						<div className="col-12 padding-small">
							<VLFTextField
								label="Oggetto"
								size="small"
								value={form.subject ?? ""}
								onChange={(e) => {
									setTextDirty(true);
									setField("subject", e.target.value);
								}}
							/>
						</div>
						<div className="col-12 padding-small">
							<VLFTextField
								label="Testo"
								size="small"
								multiline
								minRows={8}
								maxRows={14}
								value={form.body ?? ""}
								onChange={(e) => {
									setTextDirty(true);
									setField("body", e.target.value);
								}}
							/>
						</div>
					</div>
				</Section>

				<div style={{fontSize: "0.75rem", color: "#6b7a99"}} className="mt-2">
					{HAS_EMAILJS
						? "Invio tramite EmailJS."
						: CONVOCATION_WEBHOOK_URL
							? "Invio tramite servizio esterno configurato."
							: "Nessun servizio configurato: verrà aperto il client di posta (mailto)."}
				</div>
			</DialogContent>
			<DialogActions className="vlf-modal__actions">
				<Button color="error" variant="outlined" onClick={handleClose}>
					Annulla
				</Button>
				<Button
					variant="contained"
					startIcon={<SendIcon />}
					disabled={!canSend}
					onClick={send}
				>
					Invia convocazione
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConvocationModal;
